import type { PaintOrder } from './types';
/** Missing mode is the historical Over behavior; malformed input never coerces. */
export function normalizePaintOrder(value: unknown): PaintOrder | null {
  return value === undefined ? 'over' : value === 'over' || value === 'under' ? value : null;
}
export interface LayerPosition {paintOrder?:PaintOrder;belowPlacementId?:string}
const uuid=/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;
/** Absent anchors preserve legacy modes; null or malformed anchors reject. */
export function normalizeLayerAnchor(value:unknown):string|undefined|null {
  return value===undefined?undefined:typeof value==='string'&&uuid.test(value)?value.toLowerCase():null;
}
export interface PaintedLayer extends LayerPosition { id?:string;sequence: number;removed?:boolean }
/** Chronological sequences stay positive and immutable. New Under layers sort
 * below every earlier layer; new Over layers sort above every earlier layer. */
export function paintOrderRank(layer: PaintedLayer): number {
  return layer.paintOrder === 'under' ? -layer.sequence : layer.sequence;
}
export function comparePaintOrder(a: PaintedLayer, b: PaintedLayer): number {
  return paintOrderRank(a) - paintOrderRank(b);
}
export function sortPaintedLayers<T extends PaintedLayer>(layers: readonly T[]): T[] {
  // Resolve chronological insertions with a linked stack. A deep history does
  // not need fractional ranks, quadratic array splices or rewritten old rows.
  type Node={value:T;previous?:Node;next?:Node};
  let first:Node|undefined,last:Node|undefined;const ids=new Map<string,Node>();
  for(const value of [...layers].sort((a,b)=>a.sequence-b.sequence)){
    const node:Node={value},anchor=value.belowPlacementId?ids.get(value.belowPlacementId):undefined;
    if(anchor){node.next=anchor;node.previous=anchor.previous;if(anchor.previous)anchor.previous.next=node;else first=node;anchor.previous=node;}
    else if(value.paintOrder==='under'){node.next=first;if(first)first.previous=node;else last=node;first=node;}
    else{node.previous=last;if(last)last.next=node;else first=node;last=node;}
    if(value.id)ids.set(value.id,node);
  }
  const result:T[]=[];for(let node=first;node;node=node.next)result.push(node.value);return result;
}
/** Position is a gap in the saved stack: zero is bottom, length is top. */
export function pendingLayerIndex(layers:readonly (PaintedLayer&{id:string})[],position:LayerPosition):number {
  const stack=sortPaintedLayers(layers);
  if(position.belowPlacementId){const index=stack.findIndex(layer=>layer.id===position.belowPlacementId);if(index>=0)return stack.slice(0,index).filter(layer=>!layer.removed).length;}
  return position.paintOrder==='under'?0:stack.filter(layer=>!layer.removed).length;
}
export function movePendingLayer(layers:readonly (PaintedLayer&{id:string})[],position:LayerPosition,direction:'up'|'down'):LayerPosition {
  const stack=sortPaintedLayers(layers).filter(layer=>!layer.removed),index=Math.max(0,Math.min(stack.length,pendingLayerIndex(layers,position)+(direction==='up'?1:-1)));
  return index===stack.length?{paintOrder:'over'}:index===0?{paintOrder:'under'}:{paintOrder:'over',belowPlacementId:stack[index].id};
}
/** Pending artwork has no committed sequence: Under belongs below ALL saved
 * layers (including existing Under); Over belongs above all saved layers. */
export function pendingPaintOrderRank(mode?: PaintOrder): number {
  return mode === 'under' ? -Infinity : Infinity;
}
